import {
  Button,
  FormControl,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
} from '@chakra-ui/react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ArticleTag,
  ArticleTagRequest,
  articleTagsApi,
} from '@texas/api/endpoints/articleTagsApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { ErrorLabel } from '@texas/components/shared/ErrorLabel';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { HookFormCounter } from '@texas/components/shared/form/HookFormCounter';
import { SubmitButton } from '@texas/components/shared/form/SubmitButton';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';
import { useActiveSession } from '@texas/hooks/useSession';

export function ArticleTagModal({
  canEdit,
  isOpen,
  onClose,
  onSaved,
  tag,
}: {
  canEdit: boolean;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  tag: ArticleTag | null;
}) {
  const { t } = useTranslation();
  const { currentUser } = useActiveSession();
  const toast = useToast();
  const {
    request: create,
    loading: creating,
    error: createError,
  } = useApiRequest(articleTagsApi.create);
  const {
    request: update,
    loading: updating,
    error: updateError,
  } = useApiRequest(articleTagsApi.update);
  const {
    request: remove,
    loading: deleting,
    error: deleteError,
  } = useApiRequest(articleTagsApi.delete);
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<ArticleTagRequest>({ defaultValues: { name: '' } });

  useEffect(() => {
    reset({ name: tag?.name ?? '' });
  }, [isOpen, reset, tag]);

  const finish = (message: string) => {
    toast({ title: message, status: 'success', isClosable: true });
    onSaved();
    onClose();
  };

  const submit = async (request: ArticleTagRequest) => {
    const response = tag
      ? await update(tag.id, request)
      : await create(currentUser.organizationId, request);
    if (!response.hasError) {
      finish(tag ? t('general.updated') : t('general.created'));
    }
  };

  const deleteTag = async () => {
    if (!tag || !window.confirm(t('configuration.deleteArticleTag'))) return;
    const response = await remove(tag.id);
    if (!response.hasError) finish(t('general.successfullyRemoved'));
  };

  return (
    <Modal variant="entityDirectory" isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader>
          {tag ? t('general.edit') : t('general.new')}{' '}
          {t('configuration.articleTag').toLowerCase()}
        </ModalHeader>
        <form onSubmit={handleSubmit(submit)}>
          <ModalBody>
            <ErrorDetails error={createError ?? updateError ?? deleteError} />
            <FormControl isRequired={true} isInvalid={!!errors.name}>
              <TexasFormLabel>{t('general.name')}</TexasFormLabel>
              <Input
                variant="entityDirectory"
                isDisabled={!canEdit}
                {...register('name', { required: true, maxLength: 50 })}
              />
              <HookFormCounter control={control} name="name" maxLength={50} />
              <ErrorLabel text={errors.name?.message} />
            </FormControl>
          </ModalBody>
          <ModalFooter gap="2">
            {tag && canEdit && (
              <Button
                colorScheme="red"
                variant="ghost"
                isLoading={deleting}
                onClick={deleteTag}
              >
                {t('general.remove')}
              </Button>
            )}
            {canEdit && (
              <SubmitButton loading={creating || updating}>
                {t('general.save')}
              </SubmitButton>
            )}
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}
